import { Text } from "@design-system/Typography/Text";
import { ClassValue, cn } from "@design-system/Utilities";
import {
    useEffect,
    useState,
    type FC,
    type HTMLAttributes,
    type PropsWithChildren,
    type Ref,
    type RefObject,
} from "react";

export type SharedProps = {
    hint?: string;
    placeholder?: string;
    success?: boolean;
    error?: string;
    required?: boolean;
};

export type WrapperProps = SharedProps &
    HTMLAttributes<HTMLDivElement> & {
        inputRef?: RefObject<HTMLInputElement | HTMLTextAreaElement | null>;
        htmlFor?: string;
        disabled?: boolean;
        placeholderUp?: boolean;
        ref?: Ref<HTMLDivElement>;
        innerClassName?: ClassValue;
        outerClassName?: ClassValue;
    };

export const FieldWrapper: FC<PropsWithChildren<WrapperProps>> = ({
    children,
    disabled,
    htmlFor,
    hint,
    success,
    error,
    placeholder,
    inputRef,
    required,
    placeholderUp: placeholderUpProp,
    innerClassName,
    outerClassName,
}) => {
    const [placeholderUp, setPlaceholderUp] = useState(placeholderUpProp);

    // here we handle the placeholder animation, without interfeeing with the props' callbacks
    useEffect(() => {
        if (!inputRef) return;

        if (inputRef.current?.value) setPlaceholderUp(true);

        const handleInput = () => {
            setPlaceholderUp(!!inputRef?.current?.value);
        };

        const handleFocus = () => {
            setPlaceholderUp(true);
        };

        const handleBlur = () => {
            setPlaceholderUp(!!inputRef?.current?.value);
        };

        inputRef?.current?.addEventListener("input", handleInput);
        inputRef?.current?.addEventListener("blur", handleBlur);
        inputRef?.current?.addEventListener("focus", handleFocus);

        return () => {
            inputRef?.current?.removeEventListener("input", handleInput);
            inputRef?.current?.removeEventListener("blur", handleBlur);
            inputRef?.current?.removeEventListener("focus", handleFocus);
        };
    }, []);

    useEffect(() => {
        if (placeholderUpProp !== undefined)
            setPlaceholderUp(placeholderUpProp);
    }, [placeholderUpProp]);

    return (
        <div className={cn("min-w-full", outerClassName)}>
            <div
                className={cn(
                    "w-full group px-3 py-1.5 rounded-lg border border-tertiary bg-white focus-within:border-primary",
                    success && "border-[2px] !border-green",
                    disabled && "bg-transparent",
                    error && "border-[2px] !border-danger",
                    innerClassName,
                )}
            >
                <label htmlFor={htmlFor}>
                    {placeholder && (
                        <div
                            className={cn(
                                "font-regular text-secondary cursor-text transition translate-y-2.5 group-focus-within:translate-y-0",
                                placeholderUp
                                    ? " text-sm translate-y-0"
                                    : " text-base group-focus-within:text-primary ",
                                error && "!text-danger",
                            )}
                        >
                            {required ? (
                                <Text as="span" color="danger">
                                    *
                                </Text>
                            ) : null}
                            {placeholder}
                        </div>
                    )}
                    {children}
                </label>
            </div>
            {hint ? (
                <Text aria-label={hint} variant="body-sm" color="primary">
                    {hint}
                </Text>
            ) : null}
            {error ? (
                <Text variant="body-sm" color="danger">
                    {error}
                </Text>
            ) : null}
        </div>
    );
};
